import * as z from "zod";
import { userFacingError } from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import { mutator } from "./mutator";

// Route-type-scoped auth kill switches. `external_id` on a non-webpage route
// has no knob — it's a webhook's only usable auth and the browsability rule
// already bars it from serving web content.
//
// The `previous*` fields exist so the audit log records the transition, not
// just the new value; verifying them against the row keeps the log truthful
// and rejects a toggle racing another admin's change. Each knob is optional so
// a caller can toggle one without resending the others.
const setRouteAuthPolicySchema = z.object({
  publicWebpageDisabled: z.boolean().optional(),
  previousPublicWebpageDisabled: z.boolean().optional(),
  externalIdWebpageDisabled: z.boolean().optional(),
  previousExternalIdWebpageDisabled: z.boolean().optional(),
  publicNonWebpageDisabled: z.boolean().optional(),
  previousPublicNonWebpageDisabled: z.boolean().optional(),
});

export const setRouteAuthPolicy = mutator({
  schema: setRouteAuthPolicySchema,
  resolve: {},
  permissions: [
    { permission: "allow_all", scopeId: ({ ctx }) => ctx.tenantId },
  ],
  fn: async ({ tx, ctx, args }) => {
    const {
      publicWebpageDisabled,
      previousPublicWebpageDisabled,
      externalIdWebpageDisabled,
      previousExternalIdWebpageDisabled,
      publicNonWebpageDisabled,
      previousPublicNonWebpageDisabled,
    } = args;
    const tenant = await tx.run(zql.tenants.where("id", ctx.tenantId).one());
    const stale = (current: boolean, previous: boolean | undefined) =>
      current !== previous;
    if (
      publicWebpageDisabled !== undefined &&
      stale(
        tenant?.publicWebpageAuthDisabled ?? false,
        previousPublicWebpageDisabled
      )
    ) {
      throw userFacingError(
        "The setting changed since you loaded it — try again"
      );
    }
    if (
      externalIdWebpageDisabled !== undefined &&
      stale(
        tenant?.externalIdWebpageAuthDisabled ?? false,
        previousExternalIdWebpageDisabled
      )
    ) {
      throw userFacingError(
        "The setting changed since you loaded it — try again"
      );
    }
    if (
      publicNonWebpageDisabled !== undefined &&
      stale(
        tenant?.publicNonWebpageAuthDisabled ?? false,
        previousPublicNonWebpageDisabled
      )
    ) {
      throw userFacingError(
        "The setting changed since you loaded it — try again"
      );
    }
    await tx.mutate.tenants.update({
      id: ctx.tenantId,
      ...(publicWebpageDisabled !== undefined && {
        publicWebpageAuthDisabled: publicWebpageDisabled,
      }),
      ...(externalIdWebpageDisabled !== undefined && {
        externalIdWebpageAuthDisabled: externalIdWebpageDisabled,
      }),
      ...(publicNonWebpageDisabled !== undefined && {
        publicNonWebpageAuthDisabled: publicNonWebpageDisabled,
      }),
      updatedAt: Date.now(),
    });
  },
});
